import {
  Grid,
  TextField,
  Button,
  Box,
  CircularProgress,
  Autocomplete,
  Typography,
} from "@mui/material";
import { useState, useEffect } from "react";

import AssignmentIcon from "@mui/icons-material/Assignment";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";

import { createNewLegalFile } from "../../services/NewLegalFileService";
import { getLawyers, getLawyerSessionStatus } from "../../services/LawyerService";
import CardHeader from "../General/CardHeader";
import GlassCard from "../General/GlassCard";
import OnlineQueryTooltip, {
  useOnlineQueryGate,
} from "../General/OnlineQueryGate";
import { useToast } from "../../context/ToastContext";
import LawyerSessionRequiredDialog from "../General/Dialog/LawyerSessionRequiredDialog";

// Un abogado por vez: si el servidor no tiene su sesión guardada, se pide
// pegarla ANTES de disparar nada, en vez de descubrirlo después de esperar
// (ver Diagnóstico Turnstile — el login automático desde acá nunca pasa).
export default function GenerateNewsFileLegal() {
  const { showToast } = useToast();
  const { online } = useOnlineQueryGate();

  const [loading, setLoading] = useState(false);
  const [checkingSession, setCheckingSession] = useState(false);

  const [lawyers, setLawyers] = useState([]);
  const [selectedLawyer, setSelectedLawyer] = useState(null);
  const [sessionRequiredLawyer, setSessionRequiredLawyer] = useState(null);
  const [lastSent, setLastSent] = useState(null);

  const fetchLawyers = async () => {
    setLoading(true);
    try {
      const res = await getLawyers();
      setLawyers(res.data.data || []);
    } catch {
      showToast("Error al cargar los abogados.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLawyers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dispatchGeneration = async (lawyer) => {
    setLoading(true);
    try {
      const response = await createNewLegalFile({ lawyers: [lawyer.id] });
      showToast(response.data.message || "Actualización en proceso.", "success");
      setLastSent(lawyer);
      setSelectedLawyer(null);
    } catch (error) {
      if (error.response?.status === 422 || error.response?.status === 404) {
        const errors = error.response.data?.errors;
        if (errors) {
          showToast(Object.values(errors).flat().join(" "), "error");
        } else {
          showToast(error.response.data?.message || "Ocurrió un error.", "error");
        }
      } else {
        console.error(error);
        showToast("Ocurrió un error inesperado.", "error");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedLawyer) {
      showToast("Debe seleccionar un abogado.", "info");
      return;
    }

    setCheckingSession(true);
    try {
      const res = await getLawyerSessionStatus(selectedLawyer.id);
      if (!res.data?.data?.cached) {
        setSessionRequiredLawyer(selectedLawyer);
        return;
      }
    } catch {
      // Si falla la consulta de estado, se sigue igual — el propio
      // procesamiento en cola va a fallar de forma clara si hace falta.
    } finally {
      setCheckingSession(false);
    }

    await dispatchGeneration(selectedLawyer);
  };

  return (
    <Grid container justifyContent="center" sx={{ mt: 4 }}>
      <Grid size={{ xs: 12, sm: 12, md: 12, lg: 10 }}>
        <LawyerSessionRequiredDialog
          open={Boolean(sessionRequiredLawyer)}
          onClose={() => setSessionRequiredLawyer(null)}
          lawyer={sessionRequiredLawyer}
          onCaptured={(lawyer) => dispatchGeneration(lawyer)}
        />

        <GlassCard>
          <CardHeader
            icon={<AssignmentIcon />}
            title="Novedades"
            description="Actualizar expedientes con novedades judiciales. Elegí un abogado para iniciar el proceso."
          />

          <Box component="form" onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
                <Autocomplete
                  options={lawyers}
                  getOptionLabel={(option) => `${option.name} ${option.lastname}`}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  value={selectedLawyer}
                  onChange={(event, newValue) => setSelectedLawyer(newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Abogado"
                      placeholder="Seleccionar..."
                      fullWidth
                    />
                  )}
                />
              </Grid>

              {lastSent && (
                <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, opacity: 0.75 }}>
                    <CheckCircleRoundedIcon fontSize="small" color="success" />
                    <Typography sx={{ fontSize: 13.5 }}>
                      {lastSent.name} {lastSent.lastname} en proceso — podés elegir otro abogado.
                    </Typography>
                  </Box>
                </Grid>
              )}

              <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
                <OnlineQueryTooltip show={!online} block>
                  <Button
                    variant="contained"
                    color="primary"
                    type="submit"
                    fullWidth
                    disabled={loading || checkingSession || !online}
                  >
                    {loading || checkingSession ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      "Realizar Actualización"
                    )}
                  </Button>
                </OnlineQueryTooltip>
              </Grid>
            </Grid>
          </Box>
        </GlassCard>
      </Grid>
    </Grid>
  );
}
